import type { Section } from "@/components/LongPage";
import { modules } from "@/content/modules";

export type ModuleDetail = {
  category: string;
  h1: string;
  intro: string;
  heroBullets: string[];
  problems: { title: string; desc: string }[];
  capabilities: { title: string; desc: string }[];
  workflow: { n: number; title: string; body: string }[];
  roles: { role: string; pain: string; value: string }[];
  useCases: { persona: string; scenario: string; outcome: string }[];
  dataIn: string[];
  processing: string[];
  dataOut: string[];
  integrations: string[];
  implementation: string[];
  faq: { q: string; a: string }[];
  related: string[]; // module slugs
  ctaHeading: string;
  ctaBody: string;
};

const D: Record<string, ModuleDetail> = {
  "mes": {
    category: "Module · MES",
    h1: "Turn the production plan into shop-floor execution",
    intro:
      "BEEDIGIT MES connects the ERP plan with what actually happens on the shop floor. Operators receive digital job orders, machines and processes are captured in real time, and confirmed results flow back to ERP without paper travelers or end-of-shift retyping. It is the execution layer that keeps planning, production, quality and materials in sync.",
    heroBullets: [
      "Digital job orders on operator tablets",
      "Real-time WIP, scrap and consumption",
      "Bi-directional ERP exchange",
      "Start on one line, roll out plant-wide",
    ],
    problems: [
      { title: "Plans that drift from reality", desc: "ERP schedules the day, but the shop floor improvises. By evening no one can reconstruct what really ran, on which machine, for which order." },
      { title: "Paper travelers and manual entry", desc: "Operators fill in sheets that supervisors retype into ERP hours later, adding delay and typos to every confirmation." },
      { title: "No visibility on order status", desc: "Sales and planning cannot answer 'when will this order be ready' without walking the floor and asking three people." },
      { title: "Scrap and rework hidden in totals", desc: "Losses appear only in month-end reports, long after the batch that caused them left the plant." },
    ],
    capabilities: [
      { title: "Digital job orders", desc: "Every operator sees prioritised work with target quantity, standard time, drawings and instructions. Start, pause and complete actions are recorded automatically against the order." },
      { title: "Real-time WIP tracking", desc: "Each unit or batch carries context — order, operator, machine, timestamp — so WIP inventory reflects the shop floor, not last night's manual count." },
      { title: "Consumption and scrap capture", desc: "Materials issued to a job are consumed as work is confirmed. Scrap and rework are entered with reason codes and photos, tied to the batch and operator." },
      { title: "Genealogy and traceability", desc: "MES stitches raw materials, machines, operators and quality events into a full genealogy for every finished unit or batch." },
      { title: "ERP synchronization", desc: "Orders, routings and BOM come from ERP; produced quantity, consumption, scrap, labor and status flow back — validated and time-stamped." },
      { title: "Standardised operator workflow", desc: "The same tablet screen guides every operator through changeovers, quality checks and hand-offs, so best practice becomes the default, not the exception." },
    ],
    workflow: [
      { n: 1, title: "Receive the plan from ERP", body: "Production orders, BOM and routings arrive from the ERP in the form MES needs to dispatch them to work centers." },
      { n: 2, title: "Dispatch job orders", body: "MES splits orders into executable jobs by work center, sequence and priority, and pushes them to operator tablets and machine terminals." },
      { n: 3, title: "Execute and capture", body: "Operators run the job; machine signals, output, downtime and quality events are captured automatically or with one-tap confirmations." },
      { n: 4, title: "Confirm to ERP", body: "Produced quantity, scrap, material consumption and labor time are validated in MES and returned to ERP as a clean, auditable confirmation." },
      { n: 5, title: "Learn and improve", body: "Every job becomes structured history for OEE, quality and continuous-improvement analysis — no separate reporting cycle." },
    ],
    roles: [
      { role: "Plant manager", pain: "Cannot see order status without walking the floor.", value: "Live plan-vs-actual per line and per order, with alerts when jobs fall behind." },
      { role: "Production supervisor", pain: "Firefights priority changes and missing materials all shift.", value: "Dispatches jobs, sees WIP, reacts to alerts and confirms progress without paperwork." },
      { role: "Operator", pain: "Unclear instructions and duplicate reporting.", value: "One tablet with the next job, drawings, and one-tap confirmations." },
      { role: "Planner", pain: "Reworks the plan based on delayed and unreliable feedback.", value: "Trusts MES confirmations and closes orders in ERP the same day." },
    ],
    useCases: [
      { persona: "Cable & wire plant", scenario: "Drawing and extrusion lines run 24/7 with frequent product changes.", outcome: "MES dispatches jobs per drum and reconciles length, scrap and downtime against the plan every shift." },
      { persona: "Plastics converter", scenario: "Multiple presses share operators and molds.", outcome: "Job orders and mold changes are visible on the shared board; operators confirm cycles as they happen." },
      { persona: "Packaging line", scenario: "Short runs and frequent SKU changes.", outcome: "Operators complete changeovers by digital SOP and confirm output per order without touching paper." },
    ],
    dataIn: ["ERP production orders", "BOM and routings", "Work-center calendars", "Machine cycle signals", "Operator confirmations"],
    processing: ["Job dispatch by priority", "WIP context per unit / batch", "Consumption vs BOM", "Scrap and rework attribution", "Order-level status roll-up"],
    dataOut: ["Confirmed production to ERP", "Live plan-vs-actual dashboards", "Traceability records", "Shift and order reports"],
    integrations: [
      "BEEDIGIT industrial hardware for machine and process capture",
      "Operator tablets and terminals",
      "ERP systems via REST / JSON exchange",
      "OEE & Downtime, Quality Management and CMMS modules",
      "BI tools for order and plant analytics",
    ],
    implementation: [
      "Map work centers, products and routings with the plant team",
      "Connect machines and operator terminals starting with one line",
      "Configure job order flow, roles and standard reasons",
      "Validate confirmations end-to-end against ERP",
      "Roll out line by line, keeping a single data model",
    ],
    faq: [
      { q: "Do we have to replace our ERP?", a: "No. BEEDIGIT MES sits between ERP and the shop floor. ERP keeps ownership of orders, BOM and financials; MES executes the plan and returns clean confirmations." },
      { q: "Can we start on a single line?", a: "Yes. A typical rollout starts on one line or work center, proves value in weeks, then scales across the plant on the same data model." },
      { q: "How much data do operators enter manually?", a: "As little as possible. Cycle, output and downtime are captured from machine signals where available; operators confirm reasons, changeovers and quality events with one tap." },
      { q: "What if some machines are legacy?", a: "Legacy machines are connected through BEEDIGIT retrofit hardware — pulses, IO or auxiliary sensors — so they participate in the same MES workflow as newer equipment." },
      { q: "Can MES run without permanent internet?", a: "Yes. Edge hardware buffers data locally and continues execution during connectivity issues, then syncs when the link returns." },
      { q: "How does MES help continuous improvement?", a: "Every confirmed job becomes structured history for OEE, quality and cost analysis, so Kaizen and Six Sigma teams work from facts, not estimates." },
    ],
    related: ["job-orders", "oee-downtime", "quality-management", "material-flow"],
    ctaHeading: "See MES executing your plan",
    ctaBody: "Book a 30-minute demo. We will walk through your work centers, ERP touchpoints and a realistic rollout for one line first.",
  },

  "machine-monitoring": {
    category: "Module · Machine Monitoring & IIoT",
    h1: "Connect any machine and see what really happens",
    intro:
      "Machine Monitoring & IIoT is the layer that turns machines — old and new — into a reliable stream of production facts. BEEDIGIT edge hardware collects run, stop, speed and output signals, contextualises them with orders and operators, and streams them securely to the platform. It is the foundation every other BEEDIGIT module depends on.",
    heroBullets: [
      "Retrofit legacy machines without PLC changes",
      "Native connectors for PLCs and modern equipment",
      "24-hour local buffer, encrypted transport",
      "One data model across mixed brands and ages",
    ],
    problems: [
      { title: "Half the plant is invisible", desc: "New machines expose data; older ones do not. Reporting mixes real signals with rough estimates from paper logs." },
      { title: "Downtime without context", desc: "Machines stop but no one records why. Losses appear as one big 'other' bucket in weekly reports." },
      { title: "Vendor-locked data silos", desc: "Each brand ships its own portal. Consolidated plant-wide visibility never materialises." },
      { title: "Fragile connectivity", desc: "Any Wi-Fi hiccup wipes out shift data because everything relies on the cloud being reachable." },
    ],
    capabilities: [
      { title: "Universal signal capture", desc: "Run, stop, speed, output and process signals collected from PLCs, IOs, meters or retrofit sensors — every machine speaks the same language in the platform." },
      { title: "Live status boards", desc: "Colour-coded status per work center: running, idle, down. The floor sees the same picture as the office in real time." },
      { title: "Downtime context", desc: "Stops are automatically detected and enriched with operator reasons, so 'unknown downtime' shrinks shift after shift." },
      { title: "Edge resilience", desc: "BEEDIGIT edge hardware buffers up to 24 hours of data locally and keeps operator workflows running when the network drops." },
      { title: "Secure transport", desc: "Data leaves the plant only over encrypted MQTT/TLS to the BEEDIGIT platform — no direct machine exposure to the internet." },
      { title: "Alerts and event history", desc: "Configurable alerts for stops, speed loss or anomalies, with a full time-stamped event history for troubleshooting." },
    ],
    workflow: [
      { n: 1, title: "Survey machines and signals", body: "For every work center we identify what needs to be captured: run/stop, speed, output, key process parameters." },
      { n: 2, title: "Choose the right hardware", body: "Depending on the machine, we use BEEDIGIT edge hardware — retrofit sensors, IO modules or PLC connectors — as one consistent product family." },
      { n: 3, title: "Contextualise the stream", body: "Signals are tied to the current job order, product and operator, so numbers are meaningful, not just raw ticks." },
      { n: 4, title: "Publish to the platform", body: "Contextualised events feed status boards, OEE, energy, quality and CMMS modules from a single source of truth." },
    ],
    roles: [
      { role: "Plant manager", pain: "Mixed reports of estimated and real data.", value: "A single live picture of every work center on one screen." },
      { role: "Supervisor", pain: "Late discovery of stops and slowdowns.", value: "Immediate alerts and clear reasons instead of end-of-shift surprises." },
      { role: "Maintenance", pain: "No hard data behind failure claims.", value: "Time-stamped stop history to prioritise work orders." },
      { role: "IT / OT engineer", pain: "One integration per machine brand.", value: "One edge platform, one data model, one security posture." },
    ],
    useCases: [
      { persona: "Mixed plant with legacy presses", scenario: "Presses from three decades sit next to a new line.", outcome: "Both are visible on the same status board with the same OEE definition." },
      { persona: "Extrusion line", scenario: "Speed loss creates hidden capacity leaks.", outcome: "Speed vs target is monitored per product, and micro-stops surface at last." },
      { persona: "Assembly cell", scenario: "Operators report downtime on paper.", outcome: "Stops are detected automatically; operators only confirm the reason." },
    ],
    dataIn: ["PLC tags / OPC UA", "Digital and analog IO", "Retrofit sensors and meters", "Operator reason inputs"],
    processing: ["Stop detection", "Speed deviation vs target", "Context by order and operator", "Local buffering when offline"],
    dataOut: ["Live status boards", "Downtime events with reasons", "Feeds for OEE, CMMS, Energy, Quality", "Alerts and event history"],
    integrations: [
      "BEEDIGIT edge hardware (retrofit sensors, IO modules, gateways)",
      "Modern PLCs and industrial controllers where supported",
      "Operator terminals for reason and job context",
      "All BEEDIGIT modules downstream (OEE, MES, Energy, CMMS, Quality)",
    ],
    implementation: [
      "Signal survey per work center",
      "Hardware installation and validation",
      "Reason codes and status logic configured with the plant team",
      "Data validated against reality for one line before scaling",
    ],
    faq: [
      { q: "Can you connect very old machines?", a: "Yes. Retrofit sensors, pulse pick-ups, IO modules and auxiliary meters cover machines with no PLC or no accessible data ports." },
      { q: "Do we need to open our PLC programs?", a: "In most cases no. Retrofit hardware taps existing signals externally, without changes to the machine control logic." },
      { q: "What happens when the network drops?", a: "Edge hardware keeps running and stores events locally for up to 24 hours, then syncs automatically when the connection is restored." },
      { q: "How is the data secured?", a: "All traffic leaves the plant over encrypted MQTT/TLS to the BEEDIGIT platform. Machines are not exposed to the internet directly." },
      { q: "Can we start with just a few machines?", a: "Yes. A common starting point is one line or one work center; the same hardware and data model then scales to the rest of the plant." },
    ],
    related: ["oee-downtime", "energy-management", "cmms-maintenance"],
    ctaHeading: "See your machines on one live board",
    ctaBody: "Book a 30-minute demo. Bring one work center as an example and we will walk through the signals and hardware options.",
  },

  "oee-downtime": {
    category: "Module · OEE & Downtime",
    h1: "Real-time OEE with reasons that lead to action",
    intro:
      "OEE & Downtime turns raw machine signals and operator events into Availability, Performance and Quality — with the loss context needed to actually improve. Instead of month-end percentages, teams see live OEE, classified downtime and clear Pareto priorities for the next Kaizen.",
    heroBullets: [
      "Live A × P × Q per work center",
      "Reason-classified downtime, not black boxes",
      "Loss Pareto and trend analysis",
      "Improvement dashboards for daily meetings",
    ],
    problems: [
      { title: "OEE calculated once a month", desc: "By the time the number lands in a PowerPoint, the shift that caused the loss is gone." },
      { title: "'Other' is the biggest reason", desc: "Downtime is booked to a generic bucket because operators have no easy way to classify it." },
      { title: "No agreement on the number", desc: "Every department calculates OEE differently, so improvement discussions stall on definitions." },
      { title: "Improvements without evidence", desc: "Kaizen ideas are chosen by opinion instead of data, and their impact is hard to prove." },
    ],
    capabilities: [
      { title: "Live OEE per work center", desc: "Availability, Performance and Quality calculated in real time from machine signals and operator events, with a single agreed definition across the plant." },
      { title: "Downtime classification", desc: "Operators pick reasons from a short, plant-specific list; the module distinguishes planned, unplanned, changeover and speed losses." },
      { title: "Loss Pareto", desc: "Losses are ranked by impact, so improvement teams tackle the actual top losses instead of what feels loudest." },
      { title: "Shift and daily reports", desc: "Reports are ready at shift end without manual work — the same source as the live boards." },
      { title: "Trend analysis", desc: "OEE and specific losses tracked over time to confirm whether interventions actually moved the number." },
      { title: "Six Big Losses view", desc: "Losses grouped in the classic TPM structure, ready for A3 problem-solving and Kaizen events." },
    ],
    workflow: [
      { n: 1, title: "Define the OEE model", body: "Set standard cycle times, product-specific targets, and a shared downtime reason tree with the plant team." },
      { n: 2, title: "Capture events", body: "Machine signals and operator inputs create classified events — stops, slowdowns, quality holds." },
      { n: 3, title: "Calculate in real time", body: "Availability, Performance and Quality update as work happens; live boards reflect the same numbers as reports." },
      { n: 4, title: "Analyse and act", body: "Loss Pareto and trend charts drive daily meetings and Kaizen priorities." },
    ],
    roles: [
      { role: "Plant manager", pain: "Cannot compare shifts and lines fairly.", value: "One OEE definition, one live view, honest comparisons." },
      { role: "CI / Lean lead", pain: "Kaizen ideas without measurable impact.", value: "Pareto-driven priorities and before/after evidence." },
      { role: "Supervisor", pain: "Loses hours re-typing downtime notes.", value: "Live board and auto-generated shift report at end of shift." },
      { role: "Operator", pain: "Blame for stops they did not cause.", value: "Clear reason capture that separates machine, material and process issues." },
    ],
    useCases: [
      { persona: "Extrusion line", scenario: "Speed loss is the hidden killer.", outcome: "Performance component of OEE isolates speed losses per product and highlights the top offenders." },
      { persona: "Discrete assembly", scenario: "Micro-stops accumulate across a shift.", outcome: "Short stops are detected automatically and appear in the loss Pareto." },
      { persona: "Packaging plant", scenario: "Frequent changeovers dominate downtime.", outcome: "Changeover time trends per SKU pair drive SMED efforts." },
    ],
    dataIn: ["Machine run/stop, speed and output", "Operator reason inputs", "Quality confirmations (good / scrap)", "Standard cycle times per product"],
    processing: ["Availability, Performance, Quality", "Reason classification", "Loss aggregation and Pareto", "Trend and shift roll-ups"],
    dataOut: ["Live OEE boards", "Shift and daily reports", "Loss Pareto and Six Big Losses", "Improvement dashboards"],
    integrations: [
      "Machine Monitoring & IIoT for signals",
      "Job Orders for context per order and product",
      "CMMS to link downtime to maintenance actions",
      "Dashboards & AI for cross-plant analysis",
    ],
    implementation: [
      "Agree on OEE definition and cycle-time standards",
      "Configure a short, plant-specific downtime reason tree",
      "Validate first-line OEE against manual measurement",
      "Introduce Pareto reviews in daily meetings",
    ],
    faq: [
      { q: "How do you calculate Performance?", a: "Performance uses standard cycle times per product against actual output during running time, isolating speed losses from availability losses." },
      { q: "Can operators change downtime reasons afterwards?", a: "Yes. Reasons can be corrected within a configurable window; every change is logged for auditability." },
      { q: "Do we need standard times we do not have today?", a: "We help extract a first version from historical data, then refine it with the plant team during commissioning." },
      { q: "How is OEE different from utilisation?", a: "Utilisation ignores speed and quality losses. OEE combines all three, which is why it reveals real capacity that utilisation hides." },
      { q: "Can it work without an ERP?", a: "Yes, for OEE alone. When ERP is present, MES adds order and product context and OEE becomes more actionable." },
    ],
    related: ["machine-monitoring", "cmms-maintenance", "dashboards-ai"],
    ctaHeading: "See your real OEE, live",
    ctaBody: "Book a demo. We will walk through your work centers, standard times and a realistic path to trustworthy OEE.",
  },

  "material-flow": {
    category: "Module · Material Flow Control",
    h1: "Move materials without paper, without chaos",
    intro:
      "Material Flow Control governs how raw materials, WIP and finished goods move between warehouse and production. Every issue, transfer and consumption is recorded against a job and a batch, so stock is where the system says it is — and consumption reflects reality instead of the BOM.",
    heroBullets: [
      "Digital issue and consumption per job order",
      "Batch-level WIP visibility",
      "Transfer requests between cells",
      "Actual vs normative consumption",
    ],
    problems: [
      { title: "Ghost stock and hidden WIP", desc: "Warehouse and MES disagree on quantities because material moves happen on paper and are entered days later." },
      { title: "Standard consumption everywhere", desc: "ERP consumes materials per BOM, so real losses hide inside the standard and never trigger a corrective action." },
      { title: "Untraceable batches", desc: "When a quality issue appears, no one can quickly say which raw batches ended up in which finished units." },
      { title: "Operators waiting for materials", desc: "Missing components stop lines because there is no clean request-and-deliver signal from production to warehouse." },
    ],
    capabilities: [
      { title: "Material issue to jobs", desc: "Raw materials are issued to a specific job with quantity and batch, replacing paper vouchers and blind consumption." },
      { title: "Consumption confirmation", desc: "As jobs are executed, consumption is confirmed against actual output, exposing the gap with the BOM." },
      { title: "WIP by batch and location", desc: "Every semi-finished batch is trackable — where it is, which order it belongs to, and its status." },
      { title: "Transfer requests", desc: "Production requests material moves digitally; warehouse fulfils and confirms, with time stamps for both." },
      { title: "Location control", desc: "Stock locations for RM, WIP and FG are maintained in one model shared with quality holds and traceability." },
    ],
    workflow: [
      { n: 1, title: "Plan materials with the job", body: "MES generates material requirements per job order based on BOM and current stock." },
      { n: 2, title: "Request and issue", body: "Production requests, warehouse issues; both actions leave a digital trace with quantity, batch and location." },
      { n: 3, title: "Consume during execution", body: "Consumption confirmations follow real output; scrap and rework adjust the picture in real time." },
      { n: 4, title: "Reconcile with ERP", body: "Actual consumption and moves are returned to ERP for accurate stock and cost accounting." },
    ],
    roles: [
      { role: "Production supervisor", pain: "Lines stop waiting for materials.", value: "Digital transfer requests with clear responsibility." },
      { role: "Warehouse manager", pain: "Constant firefighting and manual reconciliation.", value: "Prioritised requests and real-time picture of what is on the floor." },
      { role: "Cost controller", pain: "Standard consumption hides losses.", value: "Actual vs normative consumption per product and batch." },
      { role: "Quality manager", pain: "Slow investigation of batch issues.", value: "Batch-level genealogy from RM to FG." },
    ],
    useCases: [
      { persona: "Cable & wire", scenario: "Compound drums are shared between machines.", outcome: "Every drum is tracked as WIP with actual consumed length and remainder." },
      { persona: "Plastics", scenario: "Regrind mixing rules must be respected.", outcome: "Batches are traceable end-to-end and consumption is verified against the recipe." },
      { persona: "Packaging", scenario: "Printed reels sit as WIP between processes.", outcome: "Reel batches are visible with location and next-step status." },
    ],
    dataIn: ["ERP BOM and stock", "Job orders and output", "Warehouse and cell locations", "Batch codes from RM and SFG"],
    processing: ["Requirement generation per job", "Issue and transfer workflow", "Actual vs normative consumption", "WIP batch tracking"],
    dataOut: ["Consumption to ERP", "WIP dashboards", "Traceability records", "Actual vs standard reports"],
    integrations: [
      "MES and Job Orders for material demand",
      "QR Coding & Traceability for batch identifiers",
      "Quality Management for holds and releases",
      "ERP for reconciliation and costing",
    ],
    implementation: [
      "Map locations for RM, WIP and FG",
      "Define batch policy per product family",
      "Enable request/issue flow with warehouse",
      "Reconcile first products end-to-end with ERP",
    ],
    faq: [
      { q: "Can we keep ERP as the master of stock?", a: "Yes. ERP stays master; the module records real moves and returns validated consumption for reconciliation." },
      { q: "Do we need to relabel our warehouse?", a: "Not necessarily. Existing labels can be used; when full traceability is a goal, QR labels are added gradually per product family." },
      { q: "What if operators forget to confirm consumption?", a: "The module enforces confirmations at job completion and flags gaps so nothing slips through silently." },
      { q: "Is it usable without a WMS?", a: "Yes. Many plants use it as their operational stock model on the floor even when ERP is the only inventory system." },
    ],
    related: ["qr-traceability", "mes", "quality-management"],
    ctaHeading: "Bring order to materials on the floor",
    ctaBody: "Book a demo. Bring a real product with its BOM and we will walk through issue, consumption and reconciliation.",
  },

  "qr-traceability": {
    category: "Module · QR Coding & Traceability",
    h1: "Every drum, pallet, batch and product — identified",
    intro:
      "QR Coding & Traceability gives every material, batch and product a unique digital identity. Labels are generated on BEEDIGIT industrial printers, scanned at every hand-off, and stitched into a full genealogy — so recalls, audits and customer questions are answered in minutes instead of days.",
    heroBullets: [
      "Label generation for RM, SFG, FG, drums and pallets",
      "Scan-to-confirm on operator terminals",
      "Batch genealogy with full history",
      "Fast recall and audit response",
    ],
    problems: [
      { title: "Recalls that take days", desc: "When an issue arrives from a customer, finding all affected units means digging through paper logs and spreadsheets." },
      { title: "Mislabeled or unlabeled WIP", desc: "Semi-finished goods travel without unique IDs, causing mixed batches and rework." },
      { title: "Audit anxiety", desc: "Every audit is a scramble to reconstruct which raw batches went into which finished units." },
      { title: "Manual reconciliation", desc: "Warehouse and production count the same items in different systems, wasting hours per week." },
    ],
    capabilities: [
      { title: "Label generation", desc: "QR labels for raw materials, semi-finished goods, finished goods, drums, pallets and containers, with a consistent format across the plant." },
      { title: "Industrial label printing", desc: "Labels print on BEEDIGIT industrial and mobile printers, designed for shop-floor conditions." },
      { title: "Scan-to-confirm workflow", desc: "Operators scan to start a job, confirm consumption, move a pallet or complete a batch — one clear action per event." },
      { title: "Batch genealogy", desc: "Every finished unit is linked to the raw batches, machines, operators and quality events involved in its production." },
      { title: "Traceability reports", desc: "Full forward and backward traceability in seconds, ready for audit or customer response." },
    ],
    workflow: [
      { n: 1, title: "Design the label", body: "Templates per product family cover the fields your customers and regulators expect." },
      { n: 2, title: "Print at the point of creation", body: "Labels are printed when a batch is produced, not later on a warehouse desk." },
      { n: 3, title: "Scan at every hand-off", body: "Scans confirm issue, consumption, transfer, quality release and shipping." },
      { n: 4, title: "Query genealogy on demand", body: "Any batch or serial can be traced forward and backward through the process." },
    ],
    roles: [
      { role: "Quality manager", pain: "Slow, painful recalls.", value: "Full genealogy in seconds, precise recall scope." },
      { role: "Warehouse manager", pain: "Mixed batches on pallets.", value: "One label per pallet with verified content." },
      { role: "Operator", pain: "Guessing which drum is which.", value: "Scan and see product, batch and next step." },
      { role: "Auditor / customer", pain: "Trust based on paperwork.", value: "Traceability records generated from real events." },
    ],
    useCases: [
      { persona: "Cable manufacturer", scenario: "Drum-based production with customer-specific lengths.", outcome: "Each drum is labelled and traceable from compound to customer." },
      { persona: "Food & beverage", scenario: "Regulatory traceability required end-to-end.", outcome: "Batch genealogy is queryable from any finished unit." },
      { persona: "Automotive supplier", scenario: "PPAP-level traceability for critical parts.", outcome: "Serial-level history includes machine, operator and quality events." },
    ],
    dataIn: ["Batch and product master data", "Job order events", "Warehouse and quality events"],
    processing: ["Label content composition", "Scan-to-event mapping", "Genealogy graph maintenance"],
    dataOut: ["Printed QR labels", "Traceability reports", "Recall scope queries", "Audit-ready records"],
    integrations: [
      "BEEDIGIT industrial and mobile printers",
      "BEEDIGIT wired, wireless and wearable scanners",
      "MES, Material Flow and Quality Management modules",
      "ERP for master data and shipping references",
    ],
    implementation: [
      "Design label templates per family",
      "Deploy printers and scanners on target lines",
      "Enable scan-to-confirm on key hand-offs first",
      "Extend to full genealogy as adoption grows",
    ],
    faq: [
      { q: "Can we keep our existing barcodes?", a: "Yes. The module reads existing codes when they are unique; QR is used where a richer identifier and better robustness are needed." },
      { q: "What if a label is damaged?", a: "Regeneration from the batch record is one click; the new label preserves the same identity." },
      { q: "Do we need special scanners?", a: "Standard BEEDIGIT scanners cover most cases; ruggedised, wireless and wearable options exist for harsh environments." },
      { q: "Can we start with one product family?", a: "Yes. A common approach is to start with FG and extend to WIP and RM as the labelling discipline stabilises." },
    ],
    related: ["material-flow", "quality-management", "mes"],
    ctaHeading: "Answer any traceability question in seconds",
    ctaBody: "Book a demo. Bring a product family with existing traceability requirements and we will show the end-to-end flow.",
  },

  "quality-management": {
    category: "Module · Quality Management",
    h1: "Digital quality checks tied to orders, batches and operators",
    intro:
      "Quality Management digitises inspections and connects every result to the order, machine, batch and operator that produced it. Nonconformances trigger holds, containment and CAPA on the same platform — quality stops being a folder on someone's desktop.",
    heroBullets: [
      "Digital checklists on operator tablets",
      "In-process and final inspections",
      "Nonconformance with photos and batch context",
      "Hold / release logic in one place",
    ],
    problems: [
      { title: "Paper checklists disappear", desc: "By the time defects are analysed, the sheets from that shift are already misplaced." },
      { title: "Late detection of defects", desc: "Issues are found at final inspection, after hours of the same defect were produced." },
      { title: "No link between defect and cause", desc: "Nonconformances live in one system and machine data in another; root cause takes weeks." },
      { title: "Manual COPQ estimates", desc: "Cost of poor quality is guessed rather than calculated from real events." },
    ],
    capabilities: [
      { title: "Digital checklists", desc: "Configurable check plans per product family — incoming, in-process and final — completed on the same tablet operators use for jobs." },
      { title: "In-process inspections", desc: "Sample plans triggered by output, time or operator event, catching drift before it becomes scrap." },
      { title: "Nonconformance capture", desc: "Reason codes, photos, batch, machine and operator captured together; automatic hold applied to the affected batch." },
      { title: "Hold / release logic", desc: "Batches on quality hold are visibly blocked in Material Flow until formally released, preventing accidental shipment." },
      { title: "Quality dashboards", desc: "Defect rates, first-pass yield, top defects and cost of poor quality summarised for the daily meeting." },
    ],
    workflow: [
      { n: 1, title: "Define check plans", body: "Per product family: what to check, when and by whom, with tolerances and photo requirements." },
      { n: 2, title: "Execute checks in context", body: "Checks appear on the operator tablet as part of the job, not as a separate task." },
      { n: 3, title: "Handle nonconformances", body: "Defects create a nonconformance record with automatic hold and clear containment steps." },
      { n: 4, title: "Investigate and close", body: "Root cause and CAPA are tracked with due dates; effectiveness is verified from real data." },
    ],
    roles: [
      { role: "Quality manager", pain: "Firefighting with unreliable data.", value: "Live picture of quality and structured CAPA follow-through." },
      { role: "Operator", pain: "Extra paperwork disconnected from the job.", value: "Checks integrated into the tablet workflow." },
      { role: "Production supervisor", pain: "Batches shipped that should have been held.", value: "Automatic hold enforced by the system." },
      { role: "CI lead", pain: "No hard COPQ number.", value: "Cost of defects derived from real events." },
    ],
    useCases: [
      { persona: "Cable manufacturer", scenario: "Insulation defects appear intermittently.", outcome: "In-process checks catch drift; nonconformance ties defect to machine and shift." },
      { persona: "Food & beverage", scenario: "Batch release requires documented checks.", outcome: "Release is blocked until the plan is complete and results are within tolerance." },
      { persona: "Metal processing", scenario: "Dimensional checks per unit.", outcome: "Measurements captured directly on the tablet and analysed for trends." },
    ],
    dataIn: ["Check plans per product", "Operator inputs and photos", "Machine and process signals", "Job and batch context"],
    processing: ["Sampling triggers", "Tolerance evaluation", "Hold application", "Defect classification"],
    dataOut: ["Nonconformance records", "Quality dashboards and reports", "Release / hold status", "CAPA tracking"],
    integrations: [
      "MES and Job Orders for context",
      "Material Flow for holds and releases",
      "QR Coding & Traceability for batch identity",
      "BEEDIGIT tablets, cameras and scanners for capture",
    ],
    implementation: [
      "Convert existing paper plans into digital checklists",
      "Enable in-process checks on one product family first",
      "Wire up hold/release with Material Flow",
      "Introduce CAPA workflow with defined owners",
    ],
    faq: [
      { q: "Do checks slow operators down?", a: "In practice, no. Checks are integrated into the same tablet as the job and shorter, focused plans replace long generic sheets." },
      { q: "Can we keep our reason code taxonomy?", a: "Yes. Reason codes and check parameters are configured to your existing quality system." },
      { q: "How is a batch put on hold?", a: "A nonconformance can automatically apply a hold on the batch and any downstream WIP that contains it." },
      { q: "Is it certification-ready?", a: "The module is designed to support ISO 9001 style audits with structured evidence; it is not itself a certification." },
    ],
    related: ["qr-traceability", "material-flow", "mes"],
    ctaHeading: "Make quality operational, not paper",
    ctaBody: "Book a demo. Bring an existing check plan and we will show it running on the tablet with real hold logic.",
  },

  "job-orders": {
    category: "Module · Job Orders",
    h1: "Digital job orders on every operator tablet",
    intro:
      "Job Orders takes production plans and turns them into executable, unambiguous tasks. Every operator sees the next job with priority, instructions and target, and confirms progress with a tap — no clipboards, no whiteboards, no dial-arounds.",
    heroBullets: [
      "Prioritised job list per work center",
      "Digital work instructions and drawings",
      "One-tap output and scrap confirmations",
      "Live status of every open job",
    ],
    problems: [
      { title: "Whiteboards out of date by lunch", desc: "The scheduling board reflects the morning meeting, not what happened after the first breakdown." },
      { title: "Unclear priorities", desc: "Operators default to what is easiest, not what is most urgent for the customer." },
      { title: "Lost instructions", desc: "Drawings and SOPs live in folders; the version on the floor is not always the current one." },
      { title: "Confirmations by SMS and shouting", desc: "Supervisors reconstruct progress from conversations, not data." },
    ],
    capabilities: [
      { title: "Job order dispatch", desc: "Jobs are pushed to work centers with priority, target quantity and standard time, and re-sequenced when the plan changes." },
      { title: "Operator workflow", desc: "The tablet guides start, pause, output confirmation, scrap and completion with a consistent screen." },
      { title: "Instructions and attachments", desc: "Current SOPs, drawings and photos are attached to the job — the operator sees exactly one correct version." },
      { title: "Live status", desc: "Supervisors see progress per job in real time, with alerts when jobs fall behind their target." },
      { title: "Priority changes", desc: "Priority changes flow to the floor immediately and are logged for later analysis." },
    ],
    workflow: [
      { n: 1, title: "Receive jobs from MES", body: "MES splits orders into jobs per work center and sequences them by priority." },
      { n: 2, title: "Dispatch to operators", body: "The next job appears on the operator tablet with everything needed to execute it." },
      { n: 3, title: "Execute and confirm", body: "Start, output, scrap and completion are captured with minimum friction." },
      { n: 4, title: "Roll up to plant view", body: "Supervisors and planners see progress and act on exceptions." },
    ],
    roles: [
      { role: "Operator", pain: "Ambiguity about what to run next.", value: "Clear queue with instructions and targets." },
      { role: "Supervisor", pain: "Progress reconstructed from conversations.", value: "Live status per job and per work center." },
      { role: "Planner", pain: "Priorities that do not stick on the floor.", value: "Digital dispatch that reflects the current plan." },
    ],
    useCases: [
      { persona: "Job shop", scenario: "Frequent priority changes and custom orders.", outcome: "Operators always see the current top job; priority history is auditable." },
      { persona: "Packaging", scenario: "Many SKUs with short runs.", outcome: "Changeover instructions embedded in the job; confirmations per SKU." },
      { persona: "Metal fabrication", scenario: "Drawings central to execution.", outcome: "Correct revision of drawings attached to every job." },
    ],
    dataIn: ["MES / ERP orders", "Standard times", "SOPs and drawings"],
    processing: ["Prioritisation", "Operator dispatch", "Progress and exception logic"],
    dataOut: ["Live job status", "Confirmations to MES/ERP", "Priority-change audit trail"],
    integrations: [
      "MES for order source and execution context",
      "OEE & Downtime for time and loss analysis",
      "Quality Management for in-process checks",
      "BEEDIGIT industrial tablets and terminals",
    ],
    implementation: [
      "Model work centers and roles",
      "Import first product family SOPs",
      "Enable dispatch and confirmations on one line",
      "Extend to the rest of the plant on the same pattern",
    ],
    faq: [
      { q: "Can operators run without a tablet?", a: "The primary UX is a tablet or terminal; scanners can act as lightweight input on stations where a tablet is not practical." },
      { q: "How do we handle unplanned jobs?", a: "Supervisors can add or reprioritise jobs on the fly; every change is logged." },
      { q: "Can we use our existing SOP files?", a: "Yes. PDFs, drawings and photos can be attached and versioned per job." },
    ],
    related: ["mes", "oee-downtime", "quality-management"],
    ctaHeading: "Give every operator a clear next job",
    ctaBody: "Book a demo. Bring one work center's real orders and we will show operator dispatch in action.",
  },

  "cmms-maintenance": {
    category: "Module · CMMS / Maintenance",
    h1: "Connect downtime with maintenance action",
    intro:
      "CMMS turns downtime signals and operator requests into structured work orders — with parts, technicians and history in one place. Preventive plans run on real machine hours, not calendar guesses, and MTBF/MTTR come out as a byproduct of doing the work.",
    heroBullets: [
      "Requests from operators and machine alarms",
      "Preventive plans by hours or date",
      "Technician workflow with spare parts",
      "MTBF, MTTR and downtime-linked history",
    ],
    problems: [
      { title: "Maintenance disconnected from production", desc: "The maintenance backlog and the downtime history live in different worlds." },
      { title: "Preventive plans on paper", desc: "PMs are scheduled by calendar and often missed under production pressure." },
      { title: "Firefighting culture", desc: "Every day starts with the loudest breakdown; there is no time for reliability work." },
      { title: "Spare parts surprises", desc: "The right part is not on the shelf when needed and stock is not visible to technicians." },
    ],
    capabilities: [
      { title: "Digital work requests", desc: "Operators raise requests from the tablet in one tap; machine alarms create requests automatically." },
      { title: "Preventive maintenance", desc: "PMs triggered by run hours, cycles or date, prioritised together with corrective work." },
      { title: "Corrective maintenance", desc: "Work orders capture symptoms, actions, parts and time spent, with links to the downtime event." },
      { title: "Machine hour tracking", desc: "Hours come directly from monitoring signals, so PMs are triggered by real usage." },
      { title: "Spare parts links", desc: "Parts on work orders link to stock so technicians know what is available before starting." },
      { title: "Reliability analytics", desc: "MTBF, MTTR and failure Pareto derived from real events, not spreadsheets." },
    ],
    workflow: [
      { n: 1, title: "Detect the need", body: "From an operator request, a machine alarm or a PM schedule reached." },
      { n: 2, title: "Plan the work", body: "Prioritise, assign a technician and check spare parts availability." },
      { n: 3, title: "Execute and record", body: "Technicians work from the mobile view and capture what they did and how long it took." },
      { n: 4, title: "Close and learn", body: "Downtime, work order and history all point at the same event, enabling reliability analysis." },
    ],
    roles: [
      { role: "Maintenance manager", pain: "No reliable KPIs.", value: "MTBF/MTTR from real events with drill-down." },
      { role: "Technician", pain: "Paper work orders and unclear priorities.", value: "Mobile view with parts, history and instructions." },
      { role: "Production supervisor", pain: "Slow response to breakdowns.", value: "One-tap request with visible response time." },
    ],
    useCases: [
      { persona: "Extrusion plant", scenario: "Frequent screw and gearbox issues.", outcome: "PMs triggered by real run hours reduce surprise failures." },
      { persona: "Packaging line", scenario: "Micro-stops are hidden.", outcome: "Automated stop detection creates targeted maintenance work." },
      { persona: "Multi-plant group", scenario: "Best practice not shared.", outcome: "Shared PM templates and reliability metrics per site." },
    ],
    dataIn: ["Machine alarms and hours", "Operator requests", "Existing PM plans and asset register"],
    processing: ["Prioritisation", "Assignment and scheduling", "Downtime-to-work-order linking"],
    dataOut: ["Work orders", "Spare parts demand", "MTBF/MTTR and reliability reports", "Maintenance history per asset"],
    integrations: [
      "Machine Monitoring & IIoT for hours and alarms",
      "OEE & Downtime for event linkage",
      "Andon for escalation of urgent stops",
      "ERP or dedicated stock system for spare parts",
    ],
    implementation: [
      "Import asset register and existing PMs",
      "Enable requests and alarms on target lines",
      "Wire machine hours from monitoring",
      "Introduce reliability review with real MTBF/MTTR",
    ],
    faq: [
      { q: "Can we migrate existing PMs?", a: "Yes. PM plans can be imported and adapted; hour-based triggers are set up using monitoring data." },
      { q: "Do technicians need laptops?", a: "No, the mobile view runs on standard tablets and phones used across BEEDIGIT." },
      { q: "How does it help improve reliability?", a: "Because downtime, requests, work and parts share the same data, MTBF/MTTR are trustworthy and failure Pareto is actionable." },
    ],
    related: ["machine-monitoring", "oee-downtime", "andon"],
    ctaHeading: "Move maintenance from firefighting to planning",
    ctaBody: "Book a demo. We will walk through your critical assets and show how requests, alarms and PMs come together.",
  },

  "andon": {
    category: "Module · Andon",
    h1: "Call for help. Escalate. Resolve. Learn.",
    intro:
      "Andon gives operators a fast, structured way to raise problems and guarantees they reach the right person in the right time. Response times, escalations and resolutions are logged, so 'we react quickly' becomes a number instead of an opinion.",
    heroBullets: [
      "Category-based operator calls",
      "Rule-driven escalation",
      "Live Andon boards",
      "Response and resolution history",
    ],
    problems: [
      { title: "Silent stops", desc: "A machine goes down and the right person hears about it minutes later, if at all." },
      { title: "One channel for everything", desc: "Quality, maintenance and material issues all reach the same phone, with no priority." },
      { title: "No accountability for response", desc: "Response times are anecdotal because no one records the clock." },
      { title: "No learning from calls", desc: "Once resolved, calls disappear into memory instead of the improvement backlog." },
    ],
    capabilities: [
      { title: "Operator call buttons", desc: "Categorised calls (quality, material, maintenance, safety) from the operator tablet or a dedicated station." },
      { title: "Escalation rules", desc: "If no acknowledgement in the configured time, the call escalates to the next level automatically." },
      { title: "Live Andon boards", desc: "TVs on the floor show active calls, who owns them, and how long they have been open." },
      { title: "Response tracking", desc: "Time-to-acknowledge and time-to-resolve measured per line, shift and category." },
      { title: "Resolution history", desc: "Every call retains its reason, action and duration, feeding improvement analysis." },
    ],
    workflow: [
      { n: 1, title: "Operator raises a call", body: "One tap on the tablet or a physical button opens a categorised call." },
      { n: 2, title: "Right person is notified", body: "The call is routed based on category, work center and current owner." },
      { n: 3, title: "Escalation on timeout", body: "If no acknowledgement, the call escalates to the next level automatically." },
      { n: 4, title: "Resolve and log", body: "Resolver marks action taken and root cause candidate for later analysis." },
    ],
    roles: [
      { role: "Operator", pain: "Feels ignored when calling for help.", value: "Clear channel with visible response." },
      { role: "Team leader", pain: "Overwhelmed by unstructured calls.", value: "Categorised, prioritised, escalating queue." },
      { role: "Plant manager", pain: "No metric for responsiveness.", value: "Real response and resolution times per line." },
    ],
    useCases: [
      { persona: "Assembly cell", scenario: "Missing components stop the line frequently.", outcome: "Material calls track logistics response time and highlight top causes." },
      { persona: "Extrusion", scenario: "Quality drift needs quick expert eyes.", outcome: "Quality calls route directly to the on-shift quality lead." },
      { persona: "Packaging line", scenario: "Micro-stops need fast reset.", outcome: "One-tap call and clear ownership reduce recovery time." },
    ],
    dataIn: ["Operator category taps", "Machine alarms (optional)", "Roles and shift roster"],
    processing: ["Routing by category", "Escalation timers", "Aggregation by line and shift"],
    dataOut: ["Live Andon boards", "Response and resolution metrics", "Call history for improvement"],
    integrations: [
      "Machine Monitoring & IIoT to trigger calls from alarms",
      "CMMS to convert maintenance calls into work orders",
      "Quality Management for quality calls",
      "BEEDIGIT tablets, buttons and displays",
    ],
    implementation: [
      "Agree call categories and escalation rules",
      "Roll out on one line with team leaders and supervisors",
      "Add public Andon board on the floor",
      "Introduce response-time KPI in daily meeting",
    ],
    faq: [
      { q: "Do we need physical buttons?", a: "Not necessarily. Tablet-based calls cover most cases; physical buttons and light towers are used where the environment justifies them." },
      { q: "Can calls become work orders?", a: "Yes. Maintenance calls flow into CMMS as work orders with all context preserved." },
      { q: "Can we track root causes?", a: "Yes. Resolvers log an action and candidate cause; recurring patterns are visible in the analysis view." },
    ],
    related: ["cmms-maintenance", "quality-management", "machine-monitoring"],
    ctaHeading: "Turn 'ask around' into a measurable process",
    ctaBody: "Book a demo. We will configure real call categories and show the escalation on one line.",
  },

  "energy-management": {
    category: "Module · Energy Management",
    h1: "Track energy per machine, order, batch and product",
    intro:
      "Energy Management captures consumption at machine, line, order and shift level and turns it into meaningful EnPIs — energy per meter, per kilogram, per unit. It supports ISO 50001 processes with baselines and Significant Energy Uses defined from real production data.",
    heroBullets: [
      "Energy per machine, order and product",
      "EnPIs and baselines aligned to ISO 50001",
      "Peak demand and idle-load visibility",
      "Anomaly alerts on real consumption",
    ],
    problems: [
      { title: "Energy allocated by area only", desc: "The plant knows the monthly bill but not which line, product or shift drives it." },
      { title: "Idle load ignored", desc: "Machines consume energy when they are not producing, and no one measures it." },
      { title: "Peaks trigger penalties", desc: "Demand peaks cost money and no one sees them coming." },
      { title: "Sustainability reporting on estimates", desc: "ESG reports rely on rough allocations rather than measured facts." },
    ],
    capabilities: [
      { title: "Per-machine energy", desc: "Current transformers or dedicated meters read consumption per machine, tied to run/stop and product context." },
      { title: "Per-order and per-batch energy", desc: "Consumption is allocated to the actual job and batch that caused it, not to a monthly bucket." },
      { title: "EnPIs and baselines", desc: "Energy per useful output — meter, kilogram, unit — measured against a baseline that can be revised as the process changes." },
      { title: "Idle vs productive energy", desc: "The module distinguishes energy consumed while producing from energy consumed while idle, exposing the biggest waste category." },
      { title: "Peak load visibility", desc: "Live view of aggregated demand with alerts before contractual thresholds are crossed." },
      { title: "Sustainability reporting support", desc: "Structured evidence for ISO 50001 and ESG reports based on measured events." },
    ],
    workflow: [
      { n: 1, title: "Install meters where they matter", body: "CTs and dedicated meters at machine, line or utility level, based on the Significant Energy Uses." },
      { n: 2, title: "Contextualise consumption", body: "Every kWh is tied to run status, product, order, batch and shift." },
      { n: 3, title: "Build EnPIs and baselines", body: "Definitions align to ISO 50001 and are validated with plant and energy management teams." },
      { n: 4, title: "Act on the results", body: "Alerts, idle-load reduction and peak management drive concrete savings." },
    ],
    roles: [
      { role: "Energy manager", pain: "Reports built from estimates.", value: "Measured EnPIs from real production events." },
      { role: "Plant manager", pain: "Cannot compare shifts fairly.", value: "Energy per unit per shift and per line." },
      { role: "Production supervisor", pain: "No feedback on idle machines.", value: "Idle-load alerts during breaks and shift changes." },
      { role: "Finance", pain: "Cannot allocate energy cost to products.", value: "Energy cost per product / order based on real data." },
    ],
    useCases: [
      { persona: "Cable manufacturer", scenario: "Energy is a major cost per useful meter.", outcome: "kWh per kilometre of finished cable tracked per product and machine." },
      { persona: "Plastics", scenario: "Extruders and dryers dominate consumption.", outcome: "SEC per kilogram monitored with baselines and drift alerts." },
      { persona: "Metal processing", scenario: "Furnaces cause demand peaks.", outcome: "Peak visibility avoids contractual overshoots." },
    ],
    dataIn: ["Current transformers and meters", "Utility meters (compressed air, gas, water)", "Production output and orders"],
    processing: ["Kwh allocation to jobs and batches", "EnPI computation", "Baseline comparison and anomaly detection"],
    dataOut: ["Energy dashboards", "EnPI reports and ISO 50001 evidence", "Peak and idle-load alerts", "Energy cost per unit"],
    integrations: [
      "BEEDIGIT edge hardware and meters",
      "Machine Monitoring & IIoT for run context",
      "MES / Job Orders for order and product context",
      "BI tools for corporate sustainability reporting",
    ],
    implementation: [
      "Identify Significant Energy Uses with the plant team",
      "Deploy meters at machine and utility level",
      "Define EnPIs and baselines per product family",
      "Enable alerts and integrate with daily meeting",
    ],
    faq: [
      { q: "Do we need to replace our meters?", a: "Often not. Existing meters can be integrated where accessible; new meters are added where visibility is missing." },
      { q: "How is this different from a simple metering solution?", a: "Consumption is tied to what was actually produced, so kWh becomes 'kWh per useful output', not just a raw number." },
      { q: "Can we support ISO 50001?", a: "Yes. Baselines, EnPIs and Significant Energy Uses can be structured to support ISO 50001 processes." },
      { q: "Can it start on one line?", a: "Yes. A common first step is one energy-intensive line, then expansion once the model proves itself." },
    ],
    related: ["machine-monitoring", "oee-downtime", "dashboards-ai"],
    ctaHeading: "Measure energy where it is created",
    ctaBody: "Book a demo. Bring one energy-intensive line and we will show the full model from CT to EnPI.",
  },

  "dashboards-ai": {
    category: "Module · Dashboards & AI Insights",
    h1: "Facts, not opinions — for every role",
    intro:
      "Dashboards & AI Insights presents BEEDIGIT data in role-based views for operators, supervisors, plant managers and executives. AI-supported analysis highlights loss patterns and improvement priorities, so decision-making moves from anecdote to evidence.",
    heroBullets: [
      "Operator, supervisor, plant and executive views",
      "Cross-module loss analysis",
      "Trend, drill-down and comparison",
      "AI-supported recommendations",
    ],
    problems: [
      { title: "Every meeting starts with data cleaning", desc: "Reports from different sources contradict each other and half the meeting is spent reconciling them." },
      { title: "Executives fly blind", desc: "By the time monthly PowerPoints reach the top floor, the numbers are already stale." },
      { title: "Operators see none of it", desc: "The people who can influence the numbers see them last, if at all." },
      { title: "AI experiments without production data", desc: "Data-science projects stall because there is no clean, contextual data foundation." },
    ],
    capabilities: [
      { title: "Role-based dashboards", desc: "Views tailored to operator, supervisor, plant and executive needs, all served from the same event data." },
      { title: "Cross-module analysis", desc: "OEE, downtime, energy, quality and maintenance analysed together to reveal real causes and trade-offs." },
      { title: "Trend and drill-down", desc: "Follow a KPI from plant summary down to a specific shift, line or batch." },
      { title: "AI-supported loss analysis", desc: "Assistive analysis highlights patterns in downtime, energy and quality data and proposes candidate improvement priorities." },
      { title: "Recommendations", desc: "Actionable next steps derived from data, ready for the daily or weekly meeting." },
    ],
    workflow: [
      { n: 1, title: "Consolidate the data model", body: "All modules feed the same event model; dashboards read from it, not from module-specific silos." },
      { n: 2, title: "Compose role-based views", body: "Standard packs cover the main roles; custom views are added on the same components." },
      { n: 3, title: "Deliver where decisions happen", body: "On the floor, in the supervisor office, in the plant meeting and in the executive summary." },
      { n: 4, title: "Assist with AI", body: "Automated highlights and recommendations focus attention on the top loss drivers." },
    ],
    roles: [
      { role: "Executive", pain: "Waits for month-end reports.", value: "Trusted weekly summary from real events." },
      { role: "Plant manager", pain: "Data conflicts between departments.", value: "One source of truth per KPI." },
      { role: "Supervisor", pain: "No time to build reports.", value: "Standard views ready every shift." },
      { role: "Operator", pain: "Detached from KPIs.", value: "Simple line view of own performance." },
    ],
    useCases: [
      { persona: "Multi-plant group", scenario: "Comparing OEE across plants is unreliable.", outcome: "One definition, one calculation, honest comparison." },
      { persona: "Executive review", scenario: "Weekly steering committee.", outcome: "Common pack from real data, not slides prepared overnight." },
      { persona: "Improvement team", scenario: "Kaizen priorities need evidence.", outcome: "Cross-module analysis links losses to their real drivers." },
    ],
    dataIn: ["Events from every BEEDIGIT module", "Master data on products, work centers and roles"],
    processing: ["KPI computation", "Cross-module correlation", "AI-assisted pattern detection"],
    dataOut: ["Role-based dashboards", "Executive and plant reports", "Recommendations backlog"],
    integrations: [
      "All BEEDIGIT modules as data sources",
      "BI tools via structured exports",
      "TV screens and mobile views for role delivery",
    ],
    implementation: [
      "Confirm KPI definitions with the plant and CI team",
      "Deploy standard role packs first",
      "Add custom views based on the daily meeting agenda",
      "Enable AI-supported analysis once base data is trusted",
    ],
    faq: [
      { q: "Do we need a data warehouse?", a: "Not to start. The module reads from the BEEDIGIT data model directly; exports feed a warehouse when one exists." },
      { q: "What does the AI do exactly?", a: "It highlights patterns and candidate improvement priorities from downtime, energy and quality data. It does not replace expert judgement, it focuses it." },
      { q: "Can we build custom views?", a: "Yes. Standard packs cover common roles; custom views are added on the same components." },
    ],
    related: ["oee-downtime", "energy-management", "mes"],
    ctaHeading: "One picture of the plant, for every role",
    ctaBody: "Book a demo. We will walk through the operator, supervisor, plant and executive views on real event data.",
  },
};

export function moduleDetail(slug: string): ModuleDetail | undefined {
  return D[slug];
}

/** Bundled module page details (seed source and fallback before the "module_details" collection is imported). */
export const BUNDLED_MODULE_DETAILS: (ModuleDetail & { slug: string })[] = Object.entries(D).map(([slug, d]) => ({ slug, ...d }));

/** Build LongPage sections from a module detail. */
export function moduleSections(slug: string, detail?: ModuleDetail, moduleList: { slug: string; title: string; short: string }[] = modules): Section[] {
  const d = detail ?? D[slug];
  if (!d) return [];
  const arr = <T,>(v: T[] | undefined) => (Array.isArray(v) ? v : []);
  const relatedItems = arr(d.related)
    .map((s) => moduleList.find((m) => m.slug === s))
    .filter((m): m is (typeof moduleList)[number] => Boolean(m))
    .map((m) => ({
      title: m.title.split(" — ")[0],
      desc: m.short,
      href: `/modules/${m.slug}`,
    }));

  return [
    {
      kind: "features",
      id: "problems",
      eyebrow: "Problems we solve",
      heading: "Where this module earns its place",
      items: arr(d.problems),
      cols: 2,
    },
    {
      kind: "features",
      id: "capabilities",
      eyebrow: "What it does",
      heading: "Core capabilities",
      items: arr(d.capabilities),
      cols: 3,
    },
    {
      kind: "steps",
      id: "how-it-works",
      eyebrow: "How it works",
      heading: "From signal to decision",
      items: arr(d.workflow).map((w) => ({ n: w.n, title: w.title, body: w.body })),
    },
    {
      kind: "personas",
      id: "roles",
      eyebrow: "Role-based value",
      heading: "Who uses this module and why",
      items: arr(d.roles),
    },
    {
      kind: "useCases",
      id: "use-cases",
      eyebrow: "In practice",
      heading: "Manufacturing scenarios",
      items: arr(d.useCases),
    },
    {
      kind: "split",
      id: "data-flow",
      eyebrow: "Data in · Data out",
      heading: "What goes in, what comes back",
      leftTitle: "Data in",
      rightTitle: "Data out",
      left: arr(d.dataIn),
      right: arr(d.dataOut),
    },
    {
      kind: "checklist",
      id: "processing",
      eyebrow: "Processing",
      heading: "How BEEDIGIT contextualises the data",
      items: arr(d.processing),
    },
    {
      kind: "checklist",
      id: "integrations",
      eyebrow: "Integrations & hardware",
      heading: "How it fits with the rest of BEEDIGIT",
      items: arr(d.integrations),
    },
    {
      kind: "steps",
      id: "implementation",
      eyebrow: "Implementation",
      heading: "A realistic path to production",
      items: arr(d.implementation).map((step, i) => ({
        n: i + 1,
        title: step.split(" ").slice(0, 4).join(" "),
        body: step,
      })),
    },
    {
      kind: "faq",
      id: "faq",
      eyebrow: "FAQ",
      heading: "Answers to real buyer questions",
      items: arr(d.faq),
    },
    ...(relatedItems.length
      ? [{
          kind: "related" as const,
          id: "related",
          eyebrow: "Works better together",
          heading: "Related modules",
          items: relatedItems,
        }]
      : []),
  ];
}